/**
 * AI Team Age Badge — sits next to the Ruʾya widget in the dashboard header.
 *
 * Shows the team's leading age in years. Auto-refreshes every 5 minutes so
 * the badge ticks forward automatically when the 04:00 UTC Shu'aib cycle
 * runs (each AI ages +10 years). Click it to jump to the AI Mentors page.
 *
 * Cost: one extra GET /api/ai/mentors/status per page-load + every 5 min.
 * Zero LLM cost — pure DB read.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const REFRESH_MS = 5 * 60 * 1000; // 5 minutes

export default function AITeamAgeBadge() {
  const [stats, setStats] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();

  // /ai/mentors/status requires system_admin — skip silently for everyone else
  // so we don't pollute the console with 403s on every dashboard load.
  const canFetch = user?.role === 'system_admin';

  useEffect(() => {
    if (!canFetch) return undefined;
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const { data } = await api.get('/ai/mentors/status');
        if (cancelled) return;
        const ages = (data?.personas || []).map((p) => Number(p.age_years || 0));
        if (!ages.length) return;
        const max = Math.max(...ages);
        const min = Math.min(...ages);
        const avg = Math.round(ages.reduce((s, n) => s + n, 0) / ages.length);
        setStats({ max, min, avg, count: ages.length });
      } catch {
        /* silent — never block the header on a failed mentors call */
      }
    };
    fetchStats();
    const t = setInterval(fetchStats, REFRESH_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [canFetch]);

  const display = useMemo(() => {
    if (!stats) return null;
    return stats.max.toLocaleString();
  }, [stats]);

  // Non-admin → render nothing at all (no skeleton, no API noise).
  if (!canFetch) return null;

  if (!display) {
    // Skeleton — keeps the layout from jumping. Matches Ruʾya widget pulse style.
    return (
      <div className="hidden lg:flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200/70 dark:border-white/10 bg-white/40 dark:bg-white/5 animate-pulse">
        <Sparkles size={13} className="text-slate-400 dark:text-white/40"/>
        <span className="font-mono text-[11px] text-slate-400 dark:text-white/40">— y</span>
      </div>
    );
  }

  const tooltipLines = [
    `AI Team · ${stats.count} advisors`,
    `Eldest: ${stats.max.toLocaleString()}y`,
    `Youngest: ${stats.min.toLocaleString()}y`,
    `Average: ${stats.avg.toLocaleString()}y`,
    'Click to open AI Mentors',
  ].join('\n');

  return (
    <button
      type="button"
      data-testid="ai-team-age-badge"
      onClick={() => nav('/dashboard/ai-mentors')}
      title={tooltipLines}
      className="hidden lg:flex items-center gap-2 h-9 px-3 rounded-md border border-[#8B3FC5]/30 hover:border-[#8B3FC5]
                 bg-[#FFF8E5]/60 dark:bg-[#3a2f0a]/40 transition-colors group"
    >
      <Sparkles size={13} className="text-[#5E1F8B] group-hover:text-[#8B3FC5]"/>
      <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B]">AI</span>
      <span className="font-mono text-[11px] tabular-nums text-[#7A5A00] dark:text-[#F5E1A4]">
        {display}y
      </span>
    </button>
  );
}
